import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { acceptHMRUpdate } from 'pinia';
import { licenseService } from '~/services/license-service';
import { useBillingStore } from '~/stores/person/billing';
import { LICENSE_BENEFITS, LicenseRenewalTerm, LicenseType, PurchaseType, SubscriptionType, type PurchaseLicenseFor, type StandardLicenseCostsResponse } from '~/types/licenses/licenses';
import type { License } from '~/types/person/person';


export const useSubscribeStore = defineStore('subscribe', () => {
    const purchasingFor = ref<number | null>(null);
    const purchaseType = ref<PurchaseType>(PurchaseType.PURCHASE);
    const license = ref<License | null>(null);

    const selectedTerm = ref<LicenseRenewalTerm>(LicenseRenewalTerm.YEARLY);
    const selectedPlanType = ref<LicenseType>(LicenseType.PRO);
    const selectedUserType = ref<SubscriptionType>(SubscriptionType.INDIVIDUAL);
    const licenseCount = ref(1);

    const paymentMethodId = ref('');
    const isUpdateCurrentLicense = ref(false);
    const isSimulationUpgrade = ref(false);
    const giftCertCode = ref('');
    const giftCertAmount = ref(0);
    const giftCertEmail = ref('');

    const giftCertAppliedCode = ref('');
    const giftCertAppliedAmount = ref(0);
    const giftCertName = ref('');
    // License costs from API
    const licenseCosts = ref<StandardLicenseCostsResponse | null>(null);
    const isLoadingLicenseCosts = ref(false);

    // Get billing store for credit balance
    const billingStore = useBillingStore();

    // Fetch license costs from API
    async function fetchLicenseCosts() {
        isLoadingLicenseCosts.value = true;
        try {
            licenseCosts.value = await licenseService.getLicenseCosts();
            // Also fetch credit balance when loading license costs
            await billingStore.fetchCreditBalance();
        } catch (error: any) {
            console.error('Error fetching license costs:', error);
        } finally {
            isLoadingLicenseCosts.value = false;
        }
    }

    function setGiftCertificateDetails(code: string, email: string, name: string, amount: number) {
        giftCertCode.value = code;
        giftCertEmail.value = email;
        giftCertAmount.value = amount;
        giftCertName.value = name;
    }

    function applyGiftCertificate(code: string, amount: number) {
        giftCertAppliedCode.value = code;
        giftCertAppliedAmount.value = amount;
    }

    function clearGiftCard() {
        giftCertAppliedCode.value = '';
        giftCertAppliedAmount.value = 0;
    }

    // Computed getters for purchase cost breakdown
    const totalCostBeforeDiscounts = computed(() => {
        return getTotalCostBeforeDiscounts(selectedPlanType.value);
    });

    const totalAnnualDiscount = computed(() => {
        return getTotalAnnualDiscount(selectedPlanType.value);
    });

    const totalMultiLicenseDiscount = computed(() => {
        return getTotalMultiLicenseDiscount(selectedPlanType.value);
    });

    const totalCostAfterDiscounts = computed(() => {
        return getTotalCostAfterDiscounts(selectedPlanType.value);
    });

    // Credit balance from billing store
    const creditBalance = computed(() => billingStore.creditBalance);
    const isCreditBalanceLoading = computed(() => billingStore.isCreditBalanceLoading);

    // Credit amount used in this transaction
    const creditAmountUsed = computed(() => getCreditAmountUsed(selectedPlanType.value));

    // Helper functions that accept LicenseType parameter
    function getTotalCostBeforeDiscounts(licenseType: LicenseType = selectedPlanType.value) {
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        // Total cost before any discounts = count × base_monthly × term
        const basePriceForTerm = licenseData.base_price_monthly * selectedTerm.value;
        return basePriceForTerm * licenseCount.value;
    }

    function getTotalAnnualDiscount(licenseType: LicenseType = selectedPlanType.value) {
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        // Get the cost per license after annual discount (no multi-license discount)
        const costPerLicenseAfterAnnualDiscount = getCostPerLicenseAfterAnnualDiscount(licenseType);
        
        // Calculate total cost before any discounts
        const basePriceForTerm = licenseData.base_price_monthly * selectedTerm.value;
        const totalCostBeforeDiscounts = basePriceForTerm * licenseCount.value;
        
        // Calculate total cost after annual discount only
        const totalCostAfterAnnualDiscount = costPerLicenseAfterAnnualDiscount * licenseCount.value;
        
        // Annual discount = total cost before discounts - total cost after annual discount
        return totalCostBeforeDiscounts - totalCostAfterAnnualDiscount;
    }

    function getTotalMultiLicenseDiscount(licenseType: LicenseType = selectedPlanType.value) {
        const licenseData = licenseCosts.value?.[licenseType];
        if (!licenseData) return 0;
        
        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];
        
        // Calculate what the cost would be without multi-license discounts (just annual discount)
        const costPerLicenseAfterAnnualDiscount = getCostPerLicenseAfterAnnualDiscount(licenseType);
        const costWithOnlyAnnualDiscount = costPerLicenseAfterAnnualDiscount * licenseCount.value;
        
        // Calculate the actual cost by summing up individual license prices
        let actualCost = 0;
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                actualCost += pricingTier.price_per_license;
            }
        }
        
        // Multi-license discount = cost with only annual discount - actual cost
        return costWithOnlyAnnualDiscount - actualCost;
    }

    function getTotalCostAfterDiscounts(licenseType: LicenseType = selectedPlanType.value) {
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];

        // Calculate final cost from API pricing
        let finalCost = 0;
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                finalCost += pricingTier.price_per_license;
            }
        }
        
        // Apply gift card if available
        if (giftCertAppliedAmount.value > 0) {
            const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, finalCost);
            finalCost = Math.max(0, finalCost - giftCardAmountUsed);
        }

        // Apply credit balance if available
        if (billingStore.creditBalance > 0) {
            const creditAmountUsed = Math.min(billingStore.creditBalance * 100, finalCost);
            finalCost = Math.max(0, finalCost - creditAmountUsed);
        }

        if (purchaseType.value === PurchaseType.UPGRADE) {
            finalCost = finalCost - license.value?.prorate_amount!;
        }

        return finalCost;
    }

    // Calculate the amount of credit balance used in this transaction
    function getCreditAmountUsed(licenseType: LicenseType = selectedPlanType.value) {
        if (!licenseCosts.value || billingStore.creditBalance <= 0) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        const existingLicenses = licenseData.existing_licenses;
        const existingCount = existingLicenses[selectedTerm.value === LicenseRenewalTerm.MONTHLY ? 'monthly' : 
                                               selectedTerm.value === LicenseRenewalTerm.YEARLY ? 'yearly' :
                                               selectedTerm.value === LicenseRenewalTerm.TWO_YEARS ? 'two_year' : 'three_year'];

        // Calculate cost before credit balance
        let costBeforeCredit = 0;
        for (let i = 1; i <= licenseCount.value; i++) {
            const licensePosition = existingCount + i;
            let pricingTier;
            if (licensePosition === 1) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 1);
            } else if (licensePosition === 2) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 2);
            } else if (licensePosition === 3) {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 3);
            } else {
                pricingTier = termPricing.multi_licenses.find(m => m.count === 4);
            }
            
            if (pricingTier) {
                costBeforeCredit += pricingTier.price_per_license;
            }
        }
        
        // Apply gift card if available
        if (giftCertAppliedAmount.value > 0) {
            const giftCardAmountUsed = Math.min(giftCertAppliedAmount.value * 100, costBeforeCredit);
            costBeforeCredit = Math.max(0, costBeforeCredit - giftCardAmountUsed);
        }

        // Calculate how much credit balance to use
        const creditAmountUsed = Math.min(billingStore.creditBalance * 100, costBeforeCredit);
        
        return creditAmountUsed;
    }

    // Function to get cost per license after annual discount
    function getCostPerLicenseAfterAnnualDiscount(licenseType: LicenseType = selectedPlanType.value): number {
        if (!licenseCosts.value || (licenseType === LicenseType.SIGNATURE && selectedTerm.value === LicenseRenewalTerm.MONTHLY)) {
            return 0;
        }

        const licenseData = licenseCosts.value[licenseType];
        if (!licenseData) return 0;

        const termPricing = licenseData.pricing.find(p => p.months === selectedTerm.value);
        if (!termPricing) return 0;

        // Get the pricing for the first license (no multi-license discount)
        const firstLicensePricing = termPricing.multi_licenses.find(m => m.count === 1);
        if (!firstLicensePricing) return 0;

        // This is the cost per license after annual discount
        return firstLicensePricing.price_per_license;
    }

    async function assignLicense(licenseId: number, contact: PurchaseLicenseFor) {
        const authStore = useAuthStore();
        const { showSuccess, showError, showWarning } = useGlobalToast();

        if (!authStore.user?.identity?.id) {
            showError("Error", 'User not found');
            return;
        }

        try {
            await licenseService.assignLicense(authStore.user.identity.id, licenseId, contact);
            showSuccess("Success", 'License assigned successfully');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to assign license');
        }
    }

    // Fetch credit balance when subscription modal is initialized
    async function fetchCreditBalance() {
        try {
            await billingStore.fetchCreditBalance();
        } catch (error) {
            console.error('Failed to fetch credit balance:', error);
        }
    }

    return {
        selectedTerm,
        selectedPlanType,
        licenseCount,
        benefits: LICENSE_BENEFITS,
        totalCostBeforeDiscounts,
        totalAnnualDiscount,
        totalMultiLicenseDiscount,
        totalCostAfterDiscounts,
        creditBalance,
        isCreditBalanceLoading,
        creditAmountUsed,
        getTotalCostBeforeDiscounts,
        getTotalAnnualDiscount,
        getTotalMultiLicenseDiscount,
        getTotalCostAfterDiscounts,
        getCostPerLicenseAfterAnnualDiscount,
        paymentMethodId,
        selectedUserType,
        isUpdateCurrentLicense,
        isSimulationUpgrade,
        setGiftCertificateDetails,
        applyGiftCertificate,
        clearGiftCard,
        giftCertCode,
        giftCertAmount,
        giftCertAppliedCode,
        giftCertAppliedAmount,
        giftCertEmail,
        giftCertName,
        purchasingFor,
        purchaseType,
        license,
        licenseCosts,
        isLoadingLicenseCosts,
        fetchLicenseCosts,
        assignLicense,
        fetchCreditBalance,
        getCreditAmountUsed
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSubscribeStore, import.meta.hot))
}